import { TransitLine, TransitStop, CityBounds } from '../types/map';
import { LatLng } from '../types/map';
import { tuning } from '../data/tuning';

/** True if the stop's coordinates are within the map bounds. */
export function isStopInBounds(stop: TransitStop, bounds: CityBounds): boolean {
  return (
    stop.lat >= bounds.south &&
    stop.lat <= bounds.north &&
    stop.lng >= bounds.west &&
    stop.lng <= bounds.east
  );
}

function distLL(a: { lat: number; lng: number }, b: { lat: number; lng: number }): number {
  const R = 6371000;
  const dLat = ((b.lat - a.lat) * Math.PI) / 180;
  const dLng = ((b.lng - a.lng) * Math.PI) / 180;
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((a.lat * Math.PI) / 180) * Math.cos((b.lat * Math.PI) / 180) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
}

/** Return all lines with a stop within range of the given position, with the nearest stop per line. */
export interface BoardingOption {
  line: TransitLine;
  stop: TransitStop;
  stopIndex: number; // index of the stop in line.stops
  distanceM: number;
}

export function findNearbyBoardingOptions(
  position: LatLng,
  lines: TransitLine[],
  maxRangeM: number = tuning.transitBoardRange,
): BoardingOption[] {
  const options: BoardingOption[] = [];
  for (const line of lines) {
    // Find the closest stop on this line within range
    let bestIdx = -1;
    let bestDist = Infinity;
    for (let i = 0; i < line.stops.length; i++) {
      const s = line.stops[i];
      const d = distLL(position, { lat: s.lat, lng: s.lng });
      if (d < bestDist && d <= maxRangeM) {
        bestDist = d;
        bestIdx = i;
      }
    }
    if (bestIdx >= 0) {
      options.push({
        line,
        stop: line.stops[bestIdx],
        stopIndex: bestIdx,
        distanceM: bestDist,
      });
    }
  }
  // Sort by distance
  options.sort((a, b) => a.distanceM - b.distanceM);
  return options;
}

/**
 * Advance the stop index by one in the given direction, reversing at endpoints
 * OR when the next stop would go off-map. Returns the next index AND whether
 * the direction was reversed (for UI notifications).
 */
export function advanceStopIndex(
  currentIndex: number,
  direction: 'forward' | 'backward',
  line: TransitLine,
  bounds: CityBounds,
): { nextIndex: number; shouldReverse: boolean; newDirection: 'forward' | 'backward' } {
  const totalStops = line.stops.length;
  if (totalStops <= 1) {
    return { nextIndex: currentIndex, shouldReverse: false, newDirection: direction };
  }

  const isInBounds = (idx: number) => isStopInBounds(line.stops[idx], bounds);

  if (direction === 'forward') {
    const next = currentIndex + 1;
    // Off end of array OR next stop is off-map → reverse, don't advance
    if (next >= totalStops || !isInBounds(next)) {
      return { nextIndex: currentIndex, shouldReverse: true, newDirection: 'backward' };
    }
    // Reached last in-bounds stop (next-next is end-of-array or off-map) → advance then reverse
    if (next + 1 >= totalStops || !isInBounds(next + 1)) {
      return { nextIndex: next, shouldReverse: true, newDirection: 'backward' };
    }
    return { nextIndex: next, shouldReverse: false, newDirection: 'forward' };
  } else {
    const next = currentIndex - 1;
    if (next < 0 || !isInBounds(next)) {
      return { nextIndex: currentIndex, shouldReverse: true, newDirection: 'forward' };
    }
    if (next - 1 < 0 || !isInBounds(next - 1)) {
      return { nextIndex: next, shouldReverse: true, newDirection: 'forward' };
    }
    return { nextIndex: next, shouldReverse: false, newDirection: 'backward' };
  }
}

/** Get the riding-signal strength for a given transit line type. */
export function getRideSignalStrength(lineType: TransitLine['type']): number {
  switch (lineType) {
    case 'subway': return tuning.transitRideSignalSubway;
    case 'tram': return tuning.transitRideSignalTram;
    case 'bus': return tuning.transitRideSignalBus;
    case 'ferry': return tuning.transitRideSignalFerry;
  }
}

/** Look up a transit line by ID from a city's transit data. */
export function findLineById(lines: TransitLine[], lineId: string): TransitLine | null {
  return lines.find(l => l.id === lineId) ?? null;
}

/**
 * Return the sub-polyline of a transit line's geometry between two stops,
 * in ride order. Concatenates the line's geometry ways into one polyline,
 * projects the two stops onto it by nearest vertex, then returns every
 * vertex between them (inclusive). Returns null if the line has no
 * geometry or fewer than 2 matching vertices.
 */
export function getLineSegmentBetweenStops(
  line: TransitLine,
  fromStopIndex: number,
  toStopIndex: number,
): [number, number][] | null {
  if (!line.geometry || line.geometry.length === 0) return null;
  if (
    fromStopIndex < 0 || fromStopIndex >= line.stops.length ||
    toStopIndex < 0 || toStopIndex >= line.stops.length
  ) return null;

  // Concatenate all geometry ways into one long polyline (dedupe exact repeats)
  const full: [number, number][] = [];
  for (const way of line.geometry) {
    for (const pt of way) {
      const last = full[full.length - 1];
      if (!last || last[0] !== pt[0] || last[1] !== pt[1]) full.push(pt);
    }
  }
  if (full.length < 2) return null;

  // Cumulative distances along the polyline (in meters)
  const distAlong: number[] = [0];
  for (let i = 1; i < full.length; i++) {
    const dx = (full[i][0] - full[i - 1][0]) * 111000 * Math.cos((full[i][1] * Math.PI) / 180);
    const dy = (full[i][1] - full[i - 1][1]) * 111000;
    distAlong.push(distAlong[i - 1] + Math.sqrt(dx * dx + dy * dy));
  }

  const projectStop = (stop: { lat: number; lng: number }): number => {
    let best = 0;
    let bestD = Infinity;
    for (let i = 0; i < full.length; i++) {
      const dx = (full[i][0] - stop.lng) * 111000 * Math.cos((stop.lat * Math.PI) / 180);
      const dy = (full[i][1] - stop.lat) * 111000;
      const d = dx * dx + dy * dy;
      if (d < bestD) { bestD = d; best = i; }
    }
    return distAlong[best];
  };

  const startAlong = projectStop(line.stops[fromStopIndex]);
  const endAlong = projectStop(line.stops[toStopIndex]);
  const forward = endAlong >= startAlong;
  const lo = Math.min(startAlong, endAlong);
  const hi = Math.max(startAlong, endAlong);

  const sub: [number, number][] = [];
  for (let i = 0; i < full.length; i++) {
    if (distAlong[i] >= lo && distAlong[i] <= hi) sub.push(full[i]);
  }
  if (sub.length < 2) return null;

  // Sanity check: on many transit lines the concatenated geometry isn't in
  // strict ride order (OSM relation ways can be stored in any order), which
  // makes the nearest-vertex projection above return a slice that zigzags
  // across the map and is many times longer than the actual ride. If the
  // slice length is way longer than the straight-line distance between the
  // two stops, bail to null so the caller draws a straight fallback segment.
  const sliceLen = hi - lo;
  const fromStop = line.stops[fromStopIndex];
  const toStop = line.stops[toStopIndex];
  const dxStop = (toStop.lng - fromStop.lng) * 111000 * Math.cos((fromStop.lat * Math.PI) / 180);
  const dyStop = (toStop.lat - fromStop.lat) * 111000;
  const straightDist = Math.sqrt(dxStop * dxStop + dyStop * dyStop);
  // Allow up to 2.2× the straight-line distance. Real transit routes curve,
  // but not catastrophically. A slice that's 5×+ straight distance is a
  // projection failure, not a legitimate curvy track.
  if (straightDist > 20 && sliceLen > straightDist * 2.2) return null;

  return forward ? sub : [...sub].reverse();
}

/**
 * Predict the stop index a player will be at N turns from now, given they ride
 * one stop per turn and auto-reverse at endpoints/off-map stops.
 */
export function predictFutureStop(
  currentIndex: number,
  direction: 'forward' | 'backward',
  turnsAhead: number,
  line: TransitLine,
  bounds: CityBounds,
): { stopIndex: number; direction: 'forward' | 'backward' } {
  let idx = currentIndex;
  let dir = direction;
  for (let t = 0; t < turnsAhead; t++) {
    const adv = advanceStopIndex(idx, dir, line, bounds);
    idx = adv.nextIndex;
    dir = adv.newDirection;
  }
  return { stopIndex: idx, direction: dir };
}

/**
 * Collapse consecutive stops that are within `minSpacingM` of each other into
 * a single stop. Raw OSM data often has duplicate/near-duplicate stop points
 * that waste turns when riding transit.
 */
const MIN_STOP_SPACING_M = 60;
export function dedupeStopsOnLines(lines: TransitLine[]): TransitLine[] {
  return lines.map(line => {
    if (line.stops.length < 2) return line;
    const cleaned: TransitStop[] = [line.stops[0]];
    for (let i = 1; i < line.stops.length; i++) {
      const prev = cleaned[cleaned.length - 1];
      const curr = line.stops[i];
      const d = distLL({ lat: prev.lat, lng: prev.lng }, { lat: curr.lat, lng: curr.lng });
      if (d >= MIN_STOP_SPACING_M) cleaned.push(curr);
    }
    return { ...line, stops: cleaned };
  });
}
